import React, { useEffect, useState } from 'react';
import { useExportToExcel } from '../../../@shared/hooks/useExportToExcel';
import Button from '../../../shared/Button/Button';
import { CheckIcon } from '@heroicons/react/24/outline';
import moment from 'moment';

interface ColumnDefinition {
  name: string;
  field: string;
  width?: number;
}

interface ExportExcelFormProps {
  data: any[];
  columns: ColumnDefinition[];
  filename?: string;
  onClose?: () => void;
}

export const ExportExcelForm = ({
  data = [],
  columns = [],
  filename = `Export_${moment().format('YYYY-MM-DD')}`,
  onClose
}: ExportExcelFormProps) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const { exportExcelFile } = useExportToExcel();

  useEffect(() => {
    setSelectedColumns(columns.map(col => col.field));
  }, [columns]);

  const handleColumnToggle = (field: string) => {
    setSelectedColumns(prev => {
      if (prev.includes(field)) {
        return prev.filter(col => col !== field);
      } else {
        return [...prev, field];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedColumns(columns.map(col => col.field));
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
  };

  const handleExport = () => {
    if (selectedColumns.length === 0) {
      alert('Please select at least one column to export');
      return;
    }

    const selectedColumnsDefinitions = columns.filter(col =>
      selectedColumns.includes(col.field)
    );

    exportExcelFile(selectedColumnsDefinitions, data, filename);

    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <h3 className="text-lg font-medium mb-2">Select columns to export</h3>
        <div className="flex gap-2 mb-4">
          <Button
            className="text-sm py-1"
            onClick={handleSelectAll}
          >
            Select All
          </Button>
          <Button
            className="text-sm py-1"
            onClick={handleDeselectAll}
          >
            Deselect All
          </Button>
        </div>
        <div className="border dark:border-neutral-700 rounded-lg p-2">
          {columns.map((column) => (
            <div key={column.field} className="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
              <label className="flex items-center cursor-pointer w-full">
                <input
                  type="checkbox"
                  className="form-checkbox h-5 w-5 text-primary-6000"
                  checked={selectedColumns.includes(column.field)}
                  onChange={() => handleColumnToggle(column.field)}
                />
                <span className="ml-2">{column.name}</span>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        {onClose && (
          <Button
            className="bg-gray-200 hover:bg-gray-300 text-gray-800"
            onClick={onClose}
          >
            Cancel
          </Button>
        )}
        <Button
          className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 disabled:opacity-50 disabled:hover:bg-green-600"
          onClick={handleExport}
          disabled={selectedColumns.length === 0}
        >
          <CheckIcon className="h-5 w-5"/>
          Download Excel
        </Button>
      </div>
    </div>
  );
};
